import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { IconMore, IconSearch, IconTrendingDown, IconTrendingUp } from '@/icons'
import { Card } from './Card'
import { Spinner } from './Spinner'

/** The heading block every page opens with. Exactly one h1 per page lives here. */
export function PageHeader({
  title,
  description,
  actions,
  breadcrumb,
}: {
  title: string
  description?: string
  actions?: ReactNode
  breadcrumb?: ReactNode
}) {
  return (
    <div className="mb-6">
      {breadcrumb}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl leading-7 font-semibold tracking-[-0.014em] text-text">{title}</h1>
          {description ? (
            <p className="mt-1 text-[0.8125rem] leading-5 text-text-secondary">{description}</p>
          ) : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
    </div>
  )
}

export interface StatCardProps {
  label: string
  value: ReactNode
  /** A secondary figure under the value — "of 1,204", "this week". */
  sublabel?: string
  /** Signed change against the previous period; the arrow and colour follow the sign. */
  delta?: number | null
  /** For a metric where going up is bad, such as failures. */
  isInverted?: boolean
  /** Colours the value as a problem, without inventing a trend to carry the colour. */
  isAlarming?: boolean
  icon?: ReactNode
  isLoading?: boolean
}

export function StatCard({
  label,
  value,
  sublabel,
  delta,
  isInverted = false,
  isAlarming = false,
  icon,
  isLoading = false,
}: StatCardProps) {
  const isUp = typeof delta === 'number' && delta > 0
  const isGood = isInverted ? !isUp : isUp

  return (
    <Card className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs leading-4 font-medium tracking-wide text-text-secondary uppercase">
          {label}
        </p>
        {icon ? <span className="text-text-muted">{icon}</span> : null}
      </div>

      {isLoading ? (
        <div className="flex h-8 items-center">
          <Spinner size={16} className="text-text-muted" />
        </div>
      ) : (
        <p
          data-numeric
          className={cn(
            'text-2xl leading-8 font-semibold',
            isAlarming ? 'text-danger' : 'text-text',
          )}
        >
          {value}
        </p>
      )}

      <div className="flex items-center gap-2">
        {sublabel ? <p className="text-xs leading-4 text-text-secondary">{sublabel}</p> : null}
        {typeof delta === 'number' && delta !== 0 ? (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 text-xs leading-4 font-medium',
              isGood ? 'text-success' : 'text-danger',
            )}
          >
            {isUp ? <IconTrendingUp size={12} /> : <IconTrendingDown size={12} />}
            {Math.abs(delta).toFixed(0)}%
          </span>
        ) : null}
      </div>
    </Card>
  )
}

export interface TabItem {
  readonly id: string
  readonly label: string
  readonly count?: number | undefined
}

/**
 * A tab list wired for the keyboard: arrow keys move between tabs, and only the active tab is
 * in the tab order, which is what the ARIA pattern requires.
 */
export function Tabs({
  tabs,
  activeId,
  onChange,
  className,
}: {
  tabs: readonly TabItem[]
  activeId: string
  onChange: (id: string) => void
  className?: string
}) {
  const refs = useRef(new Map<string, HTMLButtonElement>())

  function handleKeyDown(event: React.KeyboardEvent) {
    const index = tabs.findIndex((tab) => tab.id === activeId)
    if (index < 0) return

    let nextIndex: number | null = null
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length
    else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length
    else if (event.key === 'Home') nextIndex = 0
    else if (event.key === 'End') nextIndex = tabs.length - 1

    if (nextIndex === null) return

    event.preventDefault()
    const next = tabs[nextIndex]
    if (!next) return

    onChange(next.id)
    refs.current.get(next.id)?.focus()
  }

  return (
    <div
      role="tablist"
      onKeyDown={handleKeyDown}
      className={cn('flex gap-1 overflow-x-auto border-b border-border', className)}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeId

        return (
          <button
            key={tab.id}
            ref={(node) => {
              if (node) refs.current.set(tab.id, node)
              else refs.current.delete(tab.id)
            }}
            type="button"
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={isActive}
            aria-controls={`panel-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => {
              onChange(tab.id)
            }}
            className={cn(
              'relative shrink-0 px-3 py-2 text-[0.8125rem] leading-5 font-medium whitespace-nowrap',
              'transition-colors after:absolute after:inset-x-0 after:-bottom-px after:h-0.5',
              isActive
                ? 'text-text after:bg-primary'
                : 'text-text-secondary after:bg-transparent hover:text-text',
            )}
          >
            {tab.label}
            {typeof tab.count === 'number' ? (
              <span className="ml-1.5 text-xs text-text-muted" data-numeric>
                {tab.count}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

export function TabPanel({ id, children }: { id: string; children: ReactNode }) {
  return (
    <div role="tabpanel" id={`panel-${id}`} aria-labelledby={`tab-${id}`} tabIndex={0}>
      {children}
    </div>
  )
}

/**
 * Search that debounces its own input.
 *
 * The value shown is local so typing stays instant; the value reported is delayed, so the
 * server sees one request per pause rather than one per keystroke.
 */
export function SearchInput({
  value,
  onChange,
  placeholder,
  delayMs = 300,
  className,
}: {
  value: string
  onChange: (next: string) => void
  placeholder: string
  delayMs?: number
  className?: string
}) {
  const id = useId()
  const [draft, setDraft] = useState(value)
  const committed = useRef(value)

  // A change from outside — a cleared filter, a restored URL — must win over the local draft.
  useEffect(() => {
    if (value !== committed.current) {
      committed.current = value
      setDraft(value)
    }
  }, [value])

  useEffect(() => {
    if (draft === committed.current) return

    const timer = setTimeout(() => {
      committed.current = draft
      onChange(draft)
    }, delayMs)

    return () => {
      clearTimeout(timer)
    }
  }, [draft, delayMs, onChange])

  return (
    <div className={cn('relative', className)}>
      <IconSearch
        size={16}
        className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-text-muted"
      />
      <input
        id={id}
        type="search"
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value)
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        className={cn(
          'h-9 w-full rounded-control border border-border-strong/60 bg-surface',
          'pr-3 pl-8 text-sm text-text transition-colors placeholder:text-text-muted',
        )}
      />
    </div>
  )
}

export interface MenuAction {
  readonly id: string
  readonly label: string
  readonly icon?: ReactNode
  readonly isDestructive?: boolean
  readonly isDisabled?: boolean
  readonly onSelect: () => void
}

/** Distance from the trigger, and the margin the menu keeps from the viewport edge. */
const MENU_GAP = 4
const MENU_EDGE_MARGIN = 8

/**
 * A row's overflow menu.
 *
 * The popover API supplies the top layer, light-dismiss and Escape. **Placement is ours**, and
 * has to be: the first version set `position-anchor` on the popover without ever setting a
 * matching `anchor-name` on the trigger, so the anchor did not exist and every menu opened in
 * the top-left corner of the screen. CSS anchor positioning is also Chrome-only today, so even
 * spelled correctly it would have left other browsers with the same bug.
 *
 * Measuring on open costs one layout read and works everywhere.
 */
export function ActionMenu({ actions, label }: { actions: readonly MenuAction[]; label?: string }) {
  const { t } = useTranslation()
  const id = useId()
  const popoverId = `menu-${id}`

  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const place = useCallback(() => {
    const trigger = triggerRef.current
    const menu = menuRef.current
    if (!trigger || !menu) return

    const anchor = trigger.getBoundingClientRect()
    const { width, height } = menu.getBoundingClientRect()

    // Right-aligned to the trigger: the menu hangs off a control that sits at the end of a row,
    // so growing leftwards is what keeps it on screen.
    let left = anchor.right - width
    let top = anchor.bottom + MENU_GAP

    // Flip above when there is not room below but there is room above.
    if (top + height > window.innerHeight - MENU_EDGE_MARGIN && anchor.top - height > MENU_GAP) {
      top = anchor.top - height - MENU_GAP
    }

    left = Math.min(
      Math.max(MENU_EDGE_MARGIN, left),
      window.innerWidth - width - MENU_EDGE_MARGIN,
    )
    top = Math.max(MENU_EDGE_MARGIN, top)

    menu.style.left = `${String(Math.round(left))}px`
    menu.style.top = `${String(Math.round(top))}px`
  }, [])

  useEffect(() => {
    const menu = menuRef.current
    if (!menu) return

    // `beforetoggle` fires while the menu is still laid out but before it paints, so measuring
    // here means it never appears in the wrong place first.
    const onBeforeToggle = (event: Event) => {
      if ((event as ToggleEvent).newState === 'open') place()
    }

    // A popover lives in the top layer and does NOT move with the page, so a scroll would leave
    // it stranded beside where its row used to be.
    const onReposition = () => {
      if (menu.matches(':popover-open')) place()
    }

    menu.addEventListener('beforetoggle', onBeforeToggle)
    window.addEventListener('scroll', onReposition, true)
    window.addEventListener('resize', onReposition)

    return () => {
      menu.removeEventListener('beforetoggle', onBeforeToggle)
      window.removeEventListener('scroll', onReposition, true)
      window.removeEventListener('resize', onReposition)
    }
  }, [place])

  if (actions.length === 0) return null

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        popoverTarget={popoverId}
        aria-label={label ?? t('common.moreActions')}
        onClick={(event) => {
          // Stops a row-level click handler from firing underneath the menu.
          event.stopPropagation()
        }}
        className="rounded p-1 text-text-muted transition-colors hover:bg-surface-sunken hover:text-text"
      >
        <IconMore size={16} />
      </button>

      <div
        ref={menuRef}
        id={popoverId}
        popover="auto"
        className={cn(
          'm-0 min-w-44 rounded-control border border-border bg-overlay p-1 shadow-overlay',
          // `position: fixed` so the coordinates above are viewport coordinates, which is what
          // getBoundingClientRect gives. The UA stylesheet centres a popover with margin: auto;
          // inset:auto plus explicit left/top overrides that.
          'fixed inset-auto',
        )}
      >
        {actions.map((action) => (
          <button
            key={action.id}
            type="button"
            disabled={action.isDisabled ?? false}
            onClick={(event) => {
              event.stopPropagation()
              action.onSelect()
              event.currentTarget.closest<HTMLElement>('[popover]')?.hidePopover()
            }}
            className={cn(
              'flex w-full items-center gap-2 rounded-[0.375rem] px-2.5 py-1.5',
              'text-left text-[0.8125rem] leading-5 transition-colors',
              'disabled:cursor-not-allowed disabled:opacity-40',
              action.isDestructive
                ? 'text-danger hover:bg-danger-subtle'
                : 'text-text hover:bg-surface-sunken',
            )}
          >
            {action.icon}
            {action.label}
          </button>
        ))}
      </div>
    </>
  )
}
